import { MenuData } from '@/@type/menu';
import { MenuStatus } from '@/__generated__/types';
import { GET_ALL_MENU } from '@/_apis_/queries/menu';
import { GET_POS_MENUS } from '@/_apis_/queries/merchant';
import { RootState, useSelector } from '@/redux/store';
import { useLazyQuery } from '@apollo/client';
import { Icon } from '@iconify/react';
import { FormControl, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { Box } from '@mui/system';
import { FormikProps } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeepKeyOf } from '../formik/type';
import { Label } from '../styles';

export interface PromoTagsSelectProps<T> {
    fieldPath: DeepKeyOf<T>;
    formik: FormikProps<T>;
    label?: string;
}

export const PromoTagsSelector = <T,>(props: PromoTagsSelectProps<T>): JSX.Element => {
    const { t } = useTranslation();
    const { formik, fieldPath, label } = props;
    const [promotionTags, setPromotionTags] = useState<string[]>([]);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [POSMenusLoading, setPOSMenusLoading] = useState<boolean>(true);
    const [menus, setMenus] = useState<MenuData[]>([]);
    // const authMerchant = useSelector((state: RootState) => state.auth.merchant) || null;
    const authUser = useSelector((state: RootState) => state.auth.user) || null;
    // get all menu id
    const [posMenus] = useLazyQuery(GET_POS_MENUS, {
        fetchPolicy: 'network-only',
        onCompleted(response) {
            const menus = getPublishedMenus(response.posMenus);
            setMenus(menus);
            setPOSMenusLoading(false);
        }
    });

    const [appMenu] = useLazyQuery(GET_ALL_MENU, {
        fetchPolicy: 'network-only',
        onCompleted(response) {
            const menus = getPublishedMenus(response.getMenus);
            setMenus(menus);
            setPOSMenusLoading(false);
        }
    });

    useEffect(() => {
        if (authUser?.merchantId) {
            posMenus();
        } else {
            appMenu();
        }
    }, [authUser, appMenu, posMenus]);

    useEffect(() => {
        if (menus.length > 0) {
            // menu.promotionTags may be is null. if null, skip
            // menu.promotionTags element may be is null. if null, skip
            const tags = menus.reduce((acc, menu) => {
                if (menu.promotionTags) {
                    acc.push(...menu.promotionTags.filter((tag) => tag !== null));
                }
                return acc;
            }, [] as string[]);
            const uniqueTags = Array.from(new Set(tags));

            // Only update state if tags have changed
            setPromotionTags((prevTags) => {
                if (JSON.stringify(prevTags) !== JSON.stringify(uniqueTags)) {
                    return uniqueTags;
                }
                return prevTags;
            });
        }
    }, [menus]);

    const getPublishedMenus = (menus: MenuData[]): MenuData[] => {
        if (!menus) return [];
        const publishedMenus = menus.filter((menu) => menu.status === MenuStatus.Published);
        if (publishedMenus.length > 0) {
            return publishedMenus;
        }
        return [];
    };

    const handleTagsChange = (event: SelectChangeEvent<string[]>): void => {
        const selectedTag = event.target.value;
        if (Array.isArray(selectedTag)) {
            setSelectedTags(selectedTag);
            setSelectedItemsToFormik(selectedTag);
        }
    };

    const setSelectedItemsToFormik = (tags: string[]): void => {
        formik.setFieldValue(fieldPath, tags);
    };

    useEffect(() => {
        const fieldValues = formik.getFieldProps(fieldPath).value;
        if (fieldValues) {
            setSelectedTags(fieldValues);
        }
    }, [formik, fieldPath]);

    return (
        <>
            <FormControl sx={{ width: '100%' }}>
                {/* <Stack sx={{ marginBottom: '4px' }} direction="column"> */}
                <Label>{t(label || '')}</Label>
                {/* <Stack direction="row" spacing={2}> */}
                <Select multiple sx={{ width: '100%' }} value={selectedTags} onChange={handleTagsChange}>
                    <MenuItem value="placeholder" disabled>
                        Select Items
                    </MenuItem>
                    {POSMenusLoading ? (
                        <MenuItem value="__loading__" disabled>
                            Loading...
                        </MenuItem>
                    ) : (
                        promotionTags.map(
                            (tag, idx) =>
                                tag && (
                                    <MenuItem
                                        value={tag}
                                        key={idx}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <Box component="span" sx={{ flexGrow: 1 }}>
                                            {tag}
                                        </Box>
                                        {selectedTags.some((selectedTag) => selectedTag === tag) && <Icon icon="material-symbols:close" />}
                                    </MenuItem>
                                )
                        )
                    )}
                </Select>
                {/* </Stack>
        </Stack> */}
            </FormControl>
        </>
    );
};
